import { Injectable } from '@angular/core';
import {
  SgAsyncGuiContent,
  SgAsyncGuiEvent,
  SgAsyncGuiPhase,
  SgAsyncGuiPhaseType,
  SgAsyncGuiPlugin,
  SgAsyncGuiPluginContext,
  SgAsyncGuiRef,
} from '@sg/ui-kit/async-gui';
import { SgToastConfig, SgToastRef, SgToastService } from '@sg/ui-kit/core';
import { Subject } from 'rxjs';

declare module '@sg/ui-kit/async-gui/src/lib/types' {
  interface SgAsyncGuiPlugins {
    toaster: SgAsyncGuiToasterPlugin;
  }
  interface SgAsyncGuiOptions {
    toaster?: SgAsyncGuiToasterOptions;
  }
}

const DEFAULT_MESSAGES: Record<SgAsyncGuiPhaseType, SgAsyncGuiContent> = {
  run: { message: 'Updating' },
  resolve: { message: 'Success' },
  reject: { message: 'Error' },
};

export interface SgAsyncGuiToasterOptions {
  duration?: number | Partial<Record<SgAsyncGuiPhaseType, number>>;
  content?: Partial<Record<SgAsyncGuiPhaseType, SgAsyncGuiContent | ((value?: any) => SgAsyncGuiContent)>>;
  /**
   * Use a signle toaster for the entire process.
   * Applicabple only when combining `run` phase with the phases `resolve` and/or `reject`
   */
  singleToaster?: boolean;
  extraClass?: string;
}

@Injectable()
export class SgAsyncGuiToasterPlugin implements SgAsyncGuiPlugin {
  readonly name = 'toaster' as 'toaster';

  constructor(private toastService: SgToastService) {}

  open(promise: Promise<any>, ctx: SgAsyncGuiPluginContext): SgAsyncGuiRef {
    const toasterRef = {} as Record<SgAsyncGuiPhaseType, SgToastRef>;
    const events = new Subject<SgAsyncGuiEvent | SgAsyncGuiPhase>();
    const sgAsyncGuiRef = SgAsyncGuiRef.create(this.name, ctx, events.asObservable());
    const toasterOptions: SgAsyncGuiToasterOptions = ctx.options.toaster || ({} as any);

    const terminate = (value?: any, emitForceClosing?: boolean) => {
      if (emitForceClosing) {
        events.next({ name: 'event', type: 'forceClosing' });
      }
      events.complete();
      for (const key in toasterRef) {
        toasterRef[key].dismiss();
      }
    };
    sgAsyncGuiRef.closed.subscribe(terminate); // no unsub, ref container will complete.

    const phases = ctx.options.applyOn.reduce((o, p) => {
      o[p] = true;
      return o;
    }, {} as Record<SgAsyncGuiPhaseType, true>);
    const duration = this.normDuration(toasterOptions);
    const messages = this.normMessages(toasterOptions);
    const getMessage = (phase: SgAsyncGuiPhaseType, value?: any) => {
      let msg;
      if (phase === 'reject' && value?.status === 400 && typeof value?.error === 'string') {
        msg = { title: 'Operation Failed', message: value?.error };
      }
      if (phase === 'reject' && typeof value?.error === 'object' && !(toasterOptions?.content || {})[phase]) {
        msg = { title: 'Operation Failed', message: value?.error?.message ?? value?.error?.ErrorMessage };
      }
      msg = msg || messages[phase];
      return typeof msg === 'function' ? msg(value) : msg;
    };

    const mapToNewConfig = (message: SgAsyncGuiContent, duration: number): Omit<SgToastConfig, 'type'> => ({
      title: message.title,
      description: message.message,
      showCloseButton: true,
      preventAutoClose: duration === 0,
    });

    if (phases.run) {
      const singleToaster = toasterOptions.singleToaster;
      toasterRef.run = singleToaster
        ? this.toastService.wait(mapToNewConfig(getMessage('run'), duration.run)) // do not auto-close base on timeout
        : this.toastService.info(mapToNewConfig(getMessage('run'), duration.run)); // do not auto-close base on timeout
      toasterRef.run.afterOpened().subscribe(() => {
        promise
          .then((value) => {
            if (phases.resolve) {
              if (singleToaster && !toasterRef.run.closed) {
                toasterRef.run.convertTo('success', mapToNewConfig(getMessage('resolve', value), duration.resolve));
              } else {
                toasterRef.resolve = this.toastService.success(
                  mapToNewConfig(getMessage('resolve', value), duration.resolve)
                );
              }
            }
            if (!singleToaster) {
              toasterRef.run.dismiss();
            }
          })
          .catch((err) => {
            if (phases.reject) {
              if (singleToaster && !toasterRef.run.closed) {
                toasterRef.run.convertTo('error', mapToNewConfig(getMessage('reject', err), duration.reject));
              } else {
                toasterRef.reject = this.toastService.error(mapToNewConfig(getMessage('reject', err), duration.reject));
              }
            }
            if (!singleToaster) {
              toasterRef.run.dismiss();
            }
          });
      });
    } else {
      promise
        .then((value) => {
          if (phases.resolve) {
            toasterRef.resolve = this.toastService.success(
              mapToNewConfig(getMessage('resolve', value), duration.resolve)
            );
          } else {
            terminate(undefined, true);
          }
        })
        .catch((err) => {
          if (phases.reject) {
            toasterRef.reject = this.toastService.error(mapToNewConfig(getMessage('reject', err), duration.reject));
          } else {
            terminate(undefined, true);
          }
        });
    }

    return sgAsyncGuiRef;
  }

  private normDuration(toaster: SgAsyncGuiToasterOptions): Record<SgAsyncGuiPhaseType, number> {
    const durationFactory = (base) => ({ run: base, resolve: base, reject: base });
    return typeof toaster.duration === 'object'
      ? Object.assign(durationFactory(0), toaster.duration)
      : durationFactory(toaster.duration || 0);
  }

  private normMessages(
    toaster: SgAsyncGuiToasterOptions
  ): Record<SgAsyncGuiPhaseType, SgAsyncGuiContent | ((value?: any) => SgAsyncGuiContent)> {
    return Object.assign(Object.create(DEFAULT_MESSAGES), toaster.content || {});
  }
}
